// UX-Vorschlag P4: sortierbare Portfolio-Tabelle fuer /projects. Nur gerendert,
// wenn uxEnabled("ux_web_p4"); die Karten bleiben als umschaltbare Ansicht.
//
// Kennzahlen (ueberfaellig, blockiert, naechster Meilenstein, letzte
// Aktivitaet) kommen aus GET /api/portfolio. Schlaegt das fehl,
// stehen die Spalten als „—" da und oben ein sichtbarer Hinweis — keine
// Nullen, die „alles in Ordnung" behaupten.

import { useLocation } from "wouter-preact";
import { ArrowDown, ArrowUp } from "lucide-preact";
import { HealthBadge } from "../HealthBadge";
import { UserAvatar } from "../UserAvatar";
import { findUser, useUsers } from "../../store/users";
import { formatDateShort } from "../../lib/format";
import { relTime } from "../../lib/inboxTime";
import {
  sortPortfolio,
  type PortfolioRow,
  type PortfolioSort,
  type PortfolioSortKey,
} from "../../lib/portfolio";
import {
  loadProjectViewState,
  saveProjectViewState,
} from "../../routes/project/viewStatePersistence";

const COLUMNS: { key: PortfolioSortKey; label: string; numeric?: boolean }[] = [
  { key: "name", label: "Projekt" },
  { key: "health", label: "Status" },
  { key: "overdue", label: "Überfällig", numeric: true },
  { key: "blocked", label: "Blockiert", numeric: true },
  { key: "milestone", label: "Nächster Meilenstein" },
  { key: "progress", label: "Fortschritt", numeric: true },
  { key: "owner", label: "Verantwortlich" },
  { key: "activity", label: "Letzte Aktivität" },
];

/** Projekt oeffnen, Tabelle bereits auf „Überfällig" gefiltert. */
export function openProjectFiltered(projectId: number, filter: "overdue" | "all"): void {
  const s = loadProjectViewState(projectId);
  saveProjectViewState(projectId, { ...s, view: "table", tableFilter: filter });
}

export interface PortfolioTableProps {
  rows: PortfolioRow[];
  sort: PortfolioSort;
  onSort: (s: PortfolioSort) => void;
}

export function PortfolioTable({ rows, sort, onSort }: PortfolioTableProps): preact.JSX.Element {
  useUsers();
  const [, setLocation] = useLocation();
  const ownerName = (id: number): string => findUser(id)?.name ?? `User #${id}`;
  const sorted = sortPortfolio(rows, sort, ownerName);

  const toggleSort = (key: PortfolioSortKey): void => {
    if (sort.key === key) onSort({ key, dir: sort.dir === "asc" ? "desc" : "asc" });
    // Zahlen zuerst absteigend (das Groesste interessiert), Text aufsteigend.
    else onSort({ key, dir: COLUMNS.find((c) => c.key === key)?.numeric ? "desc" : "asc" });
  };

  const go = (projectId: number, filter: "overdue" | "all" | null): void => {
    if (filter) openProjectFiltered(projectId, filter);
    setLocation(`/p/${projectId}`);
  };

  const countCell = (n: number | null, projectId: number, filter: "overdue" | null): preact.JSX.Element => {
    if (n === null) return <span class="text-[color:var(--color-on-surface-variant)]">—</span>;
    if (n === 0) return <span class="text-[color:var(--color-on-surface-variant)]">0</span>;
    return (
      <button
        type="button"
        onClick={() => go(projectId, filter)}
        class="rounded px-1 font-semibold text-[color:var(--color-error)] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
        title={filter ? "Projekt gefiltert öffnen" : "Projekt öffnen"}
      >
        {n}
      </button>
    );
  };

  return (
    <div class="overflow-x-auto rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)]">
      <table class="w-full min-w-[880px] text-sm">
        <thead>
          <tr class="border-b border-[color:var(--color-outline-variant)] text-left text-xs text-[color:var(--color-on-surface-variant)]">
            {COLUMNS.map((c) => {
              const active = sort.key === c.key;
              return (
                <th
                  key={c.key}
                  scope="col"
                  aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
                  class={`px-3 py-2 font-medium ${c.numeric ? "text-right" : ""}`}
                >
                  <button
                    type="button"
                    onClick={() => toggleSort(c.key)}
                    class={`inline-flex items-center gap-1 rounded hover:text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] ${
                      active ? "text-[color:var(--color-on-surface)]" : ""
                    }`}
                  >
                    {c.label}
                    {active &&
                      (sort.dir === "asc" ? (
                        <ArrowUp size={12} aria-hidden="true" />
                      ) : (
                        <ArrowDown size={12} aria-hidden="true" />
                      ))}
                  </button>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody class="divide-y divide-[color:var(--color-outline-variant)]">
          {sorted.map((r) => {
            const p = r.project;
            return (
              <tr key={p.id} class="hover:bg-[color:var(--color-surface-container-low)]">
                <td class="px-3 py-2">
                  <button
                    type="button"
                    onClick={() => go(p.id, null)}
                    class="flex min-w-0 items-center gap-2 rounded text-left font-medium text-[color:var(--color-on-surface)] hover:text-[color:var(--color-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
                  >
                    <span
                      class="size-2 shrink-0 rounded-full"
                      style={{ background: p.color ?? "var(--color-outline)" }}
                      aria-hidden="true"
                    />
                    <span class="truncate">{p.name}</span>
                  </button>
                </td>
                <td class="px-3 py-2">
                  <HealthBadge project={p} />
                </td>
                <td class="px-3 py-2 text-right tabular-nums">{countCell(r.overdue, p.id, "overdue")}</td>
                <td class="px-3 py-2 text-right tabular-nums">{countCell(r.blocked, p.id, null)}</td>
                <td class="px-3 py-2">
                  {r.nextMilestone ? (
                    <span class="flex min-w-0 flex-col">
                      <span class="truncate text-[color:var(--color-on-surface)]">{r.nextMilestone.title}</span>
                      <span
                        class={`text-xs ${
                          r.nextMilestone.overdue
                            ? "font-medium text-[color:var(--color-error)]"
                            : "text-[color:var(--color-on-surface-variant)]"
                        }`}
                      >
                        {r.nextMilestone.overdue ? "überfällig · " : ""}
                        {formatDateShort(r.nextMilestone.date)}
                      </span>
                    </span>
                  ) : (
                    <span class="text-[color:var(--color-on-surface-variant)]">—</span>
                  )}
                </td>
                <td class="px-3 py-2 text-right">
                  <span class="inline-flex items-center gap-2">
                    <span class="h-1.5 w-16 overflow-hidden rounded-full bg-[color:var(--color-surface-container-high)]" aria-hidden="true">
                      <span class="block h-full bg-[color:var(--color-primary)]" style={{ width: `${r.progress}%` }} />
                    </span>
                    <span class="w-9 tabular-nums text-xs text-[color:var(--color-on-surface-variant)]">{r.progress} %</span>
                  </span>
                </td>
                <td class="px-3 py-2">
                  <span class="flex min-w-0 items-center gap-2">
                    <UserAvatar userId={p.owner_id} size={20} />
                    <span class="truncate text-xs text-[color:var(--color-on-surface)]">{ownerName(p.owner_id)}</span>
                  </span>
                </td>
                <td class="px-3 py-2 text-xs text-[color:var(--color-on-surface-variant)]">
                  {r.lastActivity ? relTime(r.lastActivity) : "—"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
